import { AloftPriceJSON } from '@aloft/shared-objects';
import {
  formatPropertyType,
  schAloftPrice,
  schHoaFeeFrequency,
  schPropertyInterest,
  schZoningCompliance,
} from '@aloft/toolkit-shared';
import { Block, Checkbox, Flex, Input, Select, Typography } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { Divider } from '@aloft/toolkit-sui/chakra';

import { useEffect } from 'react';
import { Controller, Form, FormProvider, SubmitErrorHandler, SubmitHandler } from 'react-hook-form';
import { z } from 'zod';

import { FormTextarea } from '../../../components/FormTextarea/FormTextarea.tsx';
import { useAppraisifyValidation } from '../../../hooks/validation/useAppraisifyValidation.ts';
import { submitEditSubjectPropertyDetailsForm } from '../../../redux/features/actions/submitEditSubjectPropertyDetailsForm.ts';
import { selectPropertyType } from '../../../redux/features/selectors/orderSelectors.ts';
import {
  selectPropertyIdentificationById,
  selectSubjectAddress,
} from '../../../redux/features/selectors/propertyDataSelectors/propertyIdentificationSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { OrderDetailsInput } from '../components/OrderDetailsInput.tsx';
import { useSubjectPropertyDetailsForm } from '../hooks/useSubjectPropertyDetailsForm.ts';
import { SubjectPropertyDetailsFormOutput } from '../schemas.ts';

// Component that calls validation hook from within form context
export const SubjectPropertyDetails = () => {
  const validation = useAppraisifyValidation();
  const {
    zoning,
    zoningCompliance,
    parcelNumber,
    legalDescription,
    propertyInterest,
    propertyInterestOther,
  } = validation.errors.OrderDetails;

  const subjectPropertyIdentification = useAppSelector((state) =>
    selectPropertyIdentificationById(state, 'subject'),
  );
  const subjectAddress = useAppSelector(selectSubjectAddress);
  const propertyType = useAppSelector((state) => formatPropertyType(selectPropertyType(state)));
  const form = useSubjectPropertyDetailsForm();
  const dispatch = useAppDispatch();
  const pud = form.watch('pud') ?? false;
  const propertyInterestValue = form.watch('propertyInterest');

  useEffect(() => {
    form.reset({
      zoning: subjectPropertyIdentification?.zoning ?? '',
      zoningCompliance: subjectPropertyIdentification?.zoningCompliance ?? '',
      legalDescription: subjectPropertyIdentification?.legalDescription ?? '',
      hoa: subjectPropertyIdentification?.hoa ?? '',
      hoaFeeAmount: subjectPropertyIdentification?.hoaFeeAmount
        ? schAloftPrice.parse(subjectPropertyIdentification?.hoaFeeAmount)
        : undefined,
      hoaFeeFrequency: subjectPropertyIdentification?.hoaFeeFrequency,
      parcelNumber: subjectPropertyIdentification?.parcelNumber ?? '',
      pud: subjectPropertyIdentification?.pud ?? false,
      propertyInterest: subjectPropertyIdentification?.propertyInterest ?? undefined,
      propertyInterestOther: subjectPropertyIdentification?.propertyInterestOther ?? '',
    });
  }, [subjectPropertyIdentification]);

  const onSubmit: SubmitHandler<SubjectPropertyDetailsFormOutput> = (data) => {
    dispatch(submitEditSubjectPropertyDetailsForm(data));
  };

  const onError: SubmitErrorHandler<
    Omit<SubjectPropertyDetailsFormOutput, 'hoaFeeAmount'> & { hoaFeeAmount?: AloftPriceJSON }
  > = (errors) => {
    console.error(errors);
  };

  const handleChange = () => {
    form
      .handleSubmit(onSubmit, onError)()
      .catch(() => {});
  };

  // Save propertyInterest immediately when it changes, even if validation fails
  const handlePropertyInterestChange = (value: string | undefined) => {
    if (value) {
      // Get current form values
      const currentValues = form.getValues();
      // Save propertyInterest immediately, even if propertyInterestOther is invalid
      dispatch(
        submitEditSubjectPropertyDetailsForm({
          ...currentValues,
          propertyInterest: value as z.infer<typeof schPropertyInterest>,
          // Clear propertyInterestOther if not "Other"
          propertyInterestOther: value !== 'Other' ? '' : currentValues.propertyInterestOther,
        } as SubjectPropertyDetailsFormOutput),
      );
    }
  };

  // Save propertyInterestOther when it changes and is valid
  const handlePropertyInterestOtherChange = (value: string) => {
    const currentValues = form.getValues();
    // Only save if propertyInterest is "Other" and the value is not empty
    if (currentValues.propertyInterest === 'Other' && value.trim().length > 0) {
      dispatch(
        submitEditSubjectPropertyDetailsForm({
          ...currentValues,
          propertyInterestOther: value,
        } as SubjectPropertyDetailsFormOutput),
      );
    }
  };

  const handlePudChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    form.setValue('pud', e.target.checked);
  };

  return (
    <FormProvider {...form}>
      <Form onChange={handleChange}>
        <Flex flexDirection="column" padding="15px">
          <Typography variant="h2">Subject Property Details</Typography>
          <Divider
            borderColor="strokeDefault"
            marginY="10px"
            borderTopWidth="1px"
            borderBottomWidth="0px"
          />
          <Flex gap="15px" lineHeight="32px" flexDirection="column">
            <Flex>
              <Block width="250px">
                <Typography variant="h4" color="textSecondary">
                  Subject Property Address
                </Typography>
              </Block>
              <Typography variant="body1" color="textPrimary">
                {subjectAddress}
              </Typography>
            </Flex>
            <Flex>
              <Block width="250px">
                <Typography variant="h4" color="textSecondary">
                  Property Type
                </Typography>
              </Block>
              <Typography variant="body1" color="textPrimary">
                {propertyType}
              </Typography>
            </Flex>
            <OrderDetailsInput
              control={form.control}
              name="zoning"
              label="Zoning"
              isRequired
              placeholderText="Zoning type"
              errorMessage={zoning?.message}
            />
            <Flex>
              <Block width="250px">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h4" color="textSecondary">
                    Zoning Compliance
                  </Typography>
                  <span style={{ color: 'red' }}> *</span>
                </div>
              </Block>
              <Block width="250px">
                <Select
                  options={schZoningCompliance.options.map((option) => ({
                    label: option,
                    value: option,
                  }))}
                  placeholder=" "
                  size="sm"
                  value={form.watch('zoningCompliance') || ''}
                  onChange={(value) => form.setValue('zoningCompliance', value)}
                  isInvalid={!!zoningCompliance}
                />
                {zoningCompliance && (
                  <Typography variant="body2" color="error">
                    {zoningCompliance.message}
                  </Typography>
                )}
              </Block>
            </Flex>
            <Flex>
              <OrderDetailsInput
                control={form.control}
                name="hoa"
                label="HOA Name"
                placeholderText="Name of the HOA"
              />
              <Block marginLeft="10px">
                <Checkbox isChecked={pud} onChange={handlePudChange}>
                  <Typography variant="body1" color="textPrimary">
                    PUD
                  </Typography>
                </Checkbox>
              </Block>
            </Flex>
            <Flex>
              <OrderDetailsInput
                control={form.control}
                name="hoaFeeAmount"
                label="HOA Fees"
                placeholderText="Dollar amount of the HOA fee"
                type="aloftPrice"
              />
              <Block marginLeft="10px" marginTop="-1px">
                <Select
                  options={schHoaFeeFrequency.options.map((option) => ({
                    label: option,
                    value: option,
                  }))}
                  size="sm"
                  value={form.watch('hoaFeeFrequency')}
                  onChange={(value) => form.setValue('hoaFeeFrequency', value)}
                />
              </Block>
            </Flex>
            <OrderDetailsInput
              control={form.control}
              name="parcelNumber"
              label="Parcel Number"
              isRequired
              placeholderText="Parcel number of the property"
              errorMessage={parcelNumber?.message}
            />
            <Flex>
              <Block width="250px">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h4" color="textSecondary">
                    Property Interest
                  </Typography>
                  <span style={{ color: 'red' }}> *</span>
                </div>
              </Block>
              <Block width="250px">
                <Controller
                  name="propertyInterest"
                  control={form.control}
                  rules={{
                    required: true,
                  }}
                  render={({ field, fieldState }) => (
                    <>
                      <Select
                        options={schPropertyInterest.options.map((option) => ({
                          label: option,
                          value: option,
                        }))}
                        placeholder=" "
                        size="sm"
                        value={field.value ?? undefined}
                        onChange={(value) => {
                          field.onChange(value);
                          if (value !== 'Other') {
                            form.setValue('propertyInterestOther', '');
                          }
                          // Save propertyInterest immediately
                          handlePropertyInterestChange(value);
                          // Trigger validation for propertyInterestOther when propertyInterest changes
                          form.trigger('propertyInterestOther').catch(() => {});
                        }}
                        isInvalid={!!propertyInterest || fieldState.invalid}
                      />
                      {(propertyInterest || fieldState.error) && (
                        <Typography variant="body2" color="error">
                          {propertyInterest?.message || fieldState.error?.message || 'Required'}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Block>
            </Flex>
            {propertyInterestValue === 'Other' && (
              <Controller
                name="propertyInterestOther"
                control={form.control}
                rules={{
                  required: propertyInterestValue === 'Other' ? 'Required' : false,
                }}
                render={({ field, fieldState }) => {
                  const formError = fieldState.error?.message;
                  const customError = propertyInterestOther?.message;
                  const hasError = formError || customError;

                  return (
                    <Flex>
                      <Block width="250px">
                        <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
                          <Typography variant="h4" color="textSecondary">
                            Property Interest (Other)
                          </Typography>
                          <span style={{ color: 'red' }}> *</span>
                        </div>
                      </Block>
                      <Block width="250px">
                        <Input
                          size="sm"
                          type="text"
                          placeholderText="Specify property interest"
                          isInvalid={!!hasError}
                          {...field}
                          onChange={(value) => {
                            field.onChange(value);
                            // Save when value changes and is valid
                            handlePropertyInterestOtherChange(value);
                          }}
                          onClear={() => {
                            field.onChange('');
                            handlePropertyInterestOtherChange('');
                          }}
                          isRequired
                        />
                        {!!hasError && (
                          <Typography variant="body2" color="error">
                            {formError || customError}
                          </Typography>
                        )}
                      </Block>
                    </Flex>
                  );
                }}
              />
            )}
            <FormTextarea
              name="legalDescription"
              control={form.control}
              label="Legal Description"
              isRequired
              placeholder="Legal Description of the property"
              customErrorMessage={legalDescription?.message}
            />
          </Flex>
        </Flex>
      </Form>
    </FormProvider>
  );
};
